"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.swapDialCode = exports.extractCountryCodeFromNumber = exports.getExampleForCountry = exports.calculateCursorPosition = exports.formatAsYouType = exports.validatePhoneNumber = void 0;
var google_libphonenumber_1 = require("google-libphonenumber");
var countryData_1 = require("./countryData");
// Helper function to check if a region code is a valid CountryCode in our system
var isValidCountryCode = function (regionCode) {
    // Use our existing getCountryByCode function to check if the country exists
    return (0, countryData_1.getCountryByCode)(regionCode) !== undefined;
};
// Initialize phoneUtil with error handling
var phoneUtil;
try {
    phoneUtil = google_libphonenumber_1.PhoneNumberUtil.getInstance();
}
catch (error) {
    console.error('Failed to initialize PhoneNumberUtil:', error);
    // Create a mock phoneUtil object with basic fallback methods
    phoneUtil = {
        parse: function () { return null; },
        isValidNumber: function () { return false; },
        format: function () { return ''; },
        getRegionCodeForNumber: function () { return undefined; },
        getNumberType: function () { return undefined; },
        getExampleNumber: function () { return null; },
    };
}
var validatePhoneNumber = function (phoneNumber, countryCode) {
    var _a;
    try {
        // If no country code provided, try to parse the number
        var parsedNumber = countryCode
            ? phoneUtil.parse(phoneNumber, countryCode)
            : phoneUtil.parse(phoneNumber);
        if (!parsedNumber) {
            return {
                isValid: false,
                formattedNumber: phoneNumber,
                countryCode: undefined,
                nationalNumber: phoneNumber,
                nationalFormatted: phoneNumber,
                errorMessage: 'Invalid phone number format',
            };
        }
        var isValid = phoneUtil.isValidNumber(parsedNumber);
        var formattedNumber = phoneUtil.format(parsedNumber, google_libphonenumber_1.PhoneNumberFormat.E164);
        var nationalFormatted = phoneUtil.format(parsedNumber, google_libphonenumber_1.PhoneNumberFormat.NATIONAL);
        var detectedCountryCode = phoneUtil.getRegionCodeForNumber(parsedNumber);
        var nationalNumber = ((_a = parsedNumber.getNationalNumber()) === null || _a === void 0 ? void 0 : _a.toString()) || '';
        var phoneNumberType = phoneUtil.getNumberType(parsedNumber);
        return {
            isValid: isValid,
            formattedNumber: formattedNumber,
            countryCode: detectedCountryCode,
            nationalNumber: nationalNumber,
            nationalFormatted: nationalFormatted,
            phoneNumberType: phoneNumberType,
            errorMessage: isValid ? undefined : 'Please enter a valid phone number',
        };
    }
    catch (error) {
        return {
            isValid: false,
            formattedNumber: phoneNumber,
            countryCode: undefined,
            nationalNumber: phoneNumber,
            nationalFormatted: phoneNumber,
            errorMessage: 'Invalid phone number format',
        };
    }
};
exports.validatePhoneNumber = validatePhoneNumber;
var formatAsYouType = function (phoneNumber, countryCode, allowInternationalFormatting) {
    if (allowInternationalFormatting === void 0) { allowInternationalFormatting = true; }
    try {
        if (!phoneNumber || phoneNumber.length === 0) {
            return phoneNumber;
        }
        var formatter = new google_libphonenumber_1.AsYouTypeFormatter(countryCode);
        var formatted = '';
        // Clean the input - only keep digits and + at the start
        var cleanedInput = phoneNumber.replace(/[^\d+]/g, '');
        // Handle international numbers with +
        if (cleanedInput.startsWith('+') && allowInternationalFormatting) {
            // For international numbers, use a generic formatter that can detect the country
            var internationalFormatter = new google_libphonenumber_1.AsYouTypeFormatter('US'); // Use US as default for international parsing
            for (var i = 0; i < cleanedInput.length; i++) {
                var char = cleanedInput.charAt(i);
                formatted = internationalFormatter.inputDigit(char);
            }
        }
        else {
            // Process each digit through the country-specific formatter
            // If international formatting is disabled and input starts with +,
            // we still format but using the selected country's rules
            var inputToFormat = !allowInternationalFormatting && cleanedInput.startsWith('+')
                ? cleanedInput.substring(1) // Remove the + for local formatting
                : cleanedInput;
            for (var i = 0; i < inputToFormat.length; i++) {
                var char = inputToFormat.charAt(i);
                formatted = formatter.inputDigit(char);
            }
            // Add back the + if it was in the original input but international formatting is disabled
            if (!allowInternationalFormatting && cleanedInput.startsWith('+')) {
                formatted = '+' + formatted;
            }
        }
        return formatted || phoneNumber;
    }
    catch (error) {
        // If formatting fails, return the original number
        return phoneNumber;
    }
};
exports.formatAsYouType = formatAsYouType;
// Helper function to calculate cursor position after formatting
var calculateCursorPosition = function (oldValue, newValue, oldCursorPos) {
    // If the values are the same, keep the same position
    if (oldValue === newValue) {
        return oldCursorPos;
    }
    // If cursor was at the end, keep it at the end
    if (oldCursorPos >= oldValue.length) {
        return newValue.length;
    }
    // Count non-formatting characters (digits and +) before cursor in old value
    var nonFormattingCharsBeforeCursor = 0;
    for (var i = 0; i < Math.min(oldCursorPos, oldValue.length); i++) {
        var char = oldValue[i];
        if (/[\d+]/.test(char)) {
            nonFormattingCharsBeforeCursor++;
        }
    }
    // Find the position in new value that has the same number of non-formatting chars
    var newPos = 0;
    var countedChars = 0;
    for (var i = 0; i < newValue.length; i++) {
        var char = newValue[i];
        if (/[\d+]/.test(char)) {
            countedChars++;
            if (countedChars > nonFormattingCharsBeforeCursor) {
                break;
            }
        }
        newPos = i + 1;
    }
    return Math.min(newPos, newValue.length);
};
exports.calculateCursorPosition = calculateCursorPosition;
var getExampleForCountry = function (countryCode) {
    try {
        var example = phoneUtil.getExampleNumber(countryCode);
        return example
            ? phoneUtil.format(example, google_libphonenumber_1.PhoneNumberFormat.NATIONAL) || ''
            : '';
    }
    catch (_a) {
        return '';
    }
};
exports.getExampleForCountry = getExampleForCountry;
var extractCountryCodeFromNumber = function (phoneNumber, defaultCountry) {
    try {
        // First try to parse as international number (with country code)
        var parsed = void 0;
        try {
            parsed = phoneUtil.parse(phoneNumber);
        }
        catch (_a) {
            // If international parsing fails and we have a default country, try with it
            if (defaultCountry) {
                parsed = phoneUtil.parse(phoneNumber, defaultCountry);
            }
            else {
                return undefined;
            }
        }
        var regionCode = phoneUtil.getRegionCodeForNumber(parsed);
        // Only return region codes that are in our supported CountryCode type
        // google-libphonenumber returns some codes like 'AC' that we don't support
        if (regionCode && isValidCountryCode(regionCode)) {
            return regionCode;
        }
        return undefined;
    }
    catch (_b) {
        return undefined;
    }
};
exports.extractCountryCodeFromNumber = extractCountryCodeFromNumber;
/**
 * Extracts the national number from a phone number and reformats it with a new country's dial code.
 * This is used when manually changing countries to properly swap dial codes.
 */
var swapDialCode = function (phoneNumber, fromCountryCode, toCountryCode) {
    try {
        // First, try to parse the existing number to extract the national part
        var nationalNumber = '';
        var parsed = null;
        // Try multiple parsing strategies to extract the national number
        try {
            // Strategy 1: Parse as international number
            if (phoneNumber.startsWith('+')) {
                parsed = phoneUtil.parse(phoneNumber);
                var nationalNum = parsed.getNationalNumber();
                nationalNumber = nationalNum ? nationalNum.toString() : '';
            }
            else {
                // Strategy 2: Parse with the original country context
                parsed = phoneUtil.parse(phoneNumber, fromCountryCode);
                var nationalNum = parsed.getNationalNumber();
                nationalNumber = nationalNum ? nationalNum.toString() : '';
            }
        }
        catch (_a) {
            // Strategy 3: Extract digits manually if parsing fails
            // Remove everything except digits
            var digitsOnly = phoneNumber.replace(/[^\d]/g, '');
            if (phoneNumber.startsWith('+')) {
                // For international numbers, try to remove the known country calling code
                var fromCountryCallingCode = phoneUtil.getCountryCodeForRegion(fromCountryCode);
                if (fromCountryCallingCode &&
                    digitsOnly.startsWith(fromCountryCallingCode.toString())) {
                    nationalNumber = digitsOnly.substring(fromCountryCallingCode.toString().length);
                }
                else {
                    // If we can't identify the calling code, use the number as-is
                    nationalNumber = digitsOnly;
                }
            }
            else {
                // For national numbers, use all digits
                nationalNumber = digitsOnly;
            }
        }
        if (!nationalNumber) {
            // If we couldn't extract a national number, return the original
            return phoneNumber;
        }
        // Now format the national number with the new country's calling code
        try {
            var newCountryCallingCode = phoneUtil.getCountryCodeForRegion(toCountryCode);
            if (!newCountryCallingCode) {
                return phoneNumber;
            }
            // Create the new international number
            var newInternationalNumber = "+".concat(newCountryCallingCode).concat(nationalNumber);
            // Parse and validate the new number
            var newParsed = phoneUtil.parse(newInternationalNumber);
            // If the new number is valid for the target country, return it formatted
            if (phoneUtil.isValidNumber(newParsed)) {
                var detectedRegion = phoneUtil.getRegionCodeForNumber(newParsed);
                if (detectedRegion === toCountryCode) {
                    return phoneUtil.format(newParsed, google_libphonenumber_1.PhoneNumberFormat.E164);
                }
            }
            // If validation fails but we have a national number, try formatting with the new country
            var fallbackFormatted = (0, exports.formatAsYouType)("+".concat(newCountryCallingCode).concat(nationalNumber), toCountryCode, true);
            return fallbackFormatted;
        }
        catch (_b) {
            // If formatting with new country fails, return original
            return phoneNumber;
        }
    }
    catch (_c) {
        // If everything fails, return the original number
        return phoneNumber;
    }
};
exports.swapDialCode = swapDialCode;
//# sourceMappingURL=phoneValidation.js.map